import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { User } from '@/shared/types'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { IconButton } from '@/components/ui/icon-button'
import { getUserInitials } from '@/shared/lib/user-initials'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { localizeInternalPath } from '@/shared/i18n/localized-path'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { isPublicPageHidden } from '@/shared/seo/hidden-public-pages'

export interface IAccountMenuProps {
  user: User | null
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  showUserInfo?: boolean
}

export const AccountMenu = ({
  user,
  onClick,
  align = 'end',
  side = 'bottom',
  showUserInfo = false,
}: IAccountMenuProps) => {
  const { t } = useTranslation('common')
  const lang = useCurrentLang()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          ariaLabel={t('openAccountMenu')}
          variant="ghost"
          size={showUserInfo ? 'default' : 'icon'}
          className={
            showUserInfo
              ? 'h-auto w-full justify-start rounded-md px-2 py-2 text-left hover:bg-sidebar-accent/70'
              : 'relative h-8 w-8 rounded-full border border-input hover:border-primary/30'
          }
          icon={
            <span className="flex min-w-0 items-center gap-2">
              <Avatar className="h-8 w-8 shrink-0 border border-input">
                <AvatarImage src="" alt={user?.displayName} />
                <AvatarFallback>{getUserInitials(user)}</AvatarFallback>
              </Avatar>
              {showUserInfo ? (
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {user?.displayName ?? ''}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {user?.email}
                  </span>
                </span>
              ) : null}
            </span>
          }
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        align={align}
        side={side}
        forceMount
      >
        {!showUserInfo ? (
          <DropdownMenuItem asChild className="flex flex-col items-start">
            <Link to="/$lang/app" params={{ lang }} className="w-full">
              <div className="text-sm font-medium">
                {user?.displayName ?? ''}
              </div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild>
          <Link to="/$lang/settings" params={{ lang }} className="w-full">
            {t('accountSettings')}
          </Link>
        </DropdownMenuItem>
        {isPublicPageHidden('trust') ? null : (
          <DropdownMenuItem asChild className="sm:hidden">
            <a href={localizeInternalPath('/trust', lang)} className="w-full">
              {t('trustSecurity')}
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onClick}>{t('logout')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
